/**
 * Inventory store (M3.3a C2-C4).
 *
 * Holds the local player's inventory, equipment, and the currently-open
 * container (if any). Populated from server events / snapshots. The
 * inventory panel, equipment panel, and container panel all subscribe
 * to narrow selectors from this store.
 *
 * Design: Zustand, flat, focused on the viewer's inventory state.
 * The WorldModel carries per-entity inventory maps for all entities;
 * this store narrows to the local player and adds UI-specific state
 * (open container, drag source).
 */

import { create } from 'zustand';

import type {
  AccessLevel,
  EquippedItems,
  PropId,
  PropInventory,
  PropStackWire,
} from '../wire';

export interface ContainerView {
  prop_id: PropId;
  stacks: PropStackWire[];
  capacity_weight: number;
  current_weight: number;
  access: AccessLevel;
}

export interface InventoryState {
  /** Local player's inventory. Null before server populates it. */
  inventory: PropInventory | null;
  /** Local player's equipment. Null before server populates it. */
  equipment: EquippedItems | null;
  /** Currently open container (click on container prop). Null when closed. */
  openContainer: ContainerView | null;
  /** Whether access to the open container was denied. */
  accessDenied: boolean;

  // ─── Actions ─────────────────────────────────────────────────────────
  setInventory: (inv: PropInventory) => void;
  setEquipment: (eq: EquippedItems) => void;
  openContainerPanel: (container: ContainerView) => void;
  closeContainerPanel: () => void;
  setAccessDenied: (denied: boolean) => void;
  reset: () => void;
}

export const useInventory = create<InventoryState>((set) => ({
  inventory: null,
  equipment: null,
  openContainer: null,
  accessDenied: false,

  setInventory: (inv) => set({ inventory: inv }),
  setEquipment: (eq) => set({ equipment: eq }),
  openContainerPanel: (container) =>
    set({ openContainer: container, accessDenied: false }),
  closeContainerPanel: () =>
    set({ openContainer: null, accessDenied: false }),
  setAccessDenied: (denied) => set({ accessDenied: denied }),
  reset: () =>
    set({
      inventory: null,
      equipment: null,
      openContainer: null,
      accessDenied: false,
    }),
}));
